<script setup lang="ts">
import Dialog from '@/components/Dialog/Dialog.vue';
import Button from '@/components/buttons/Button.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { useToggle } from '@vueuse/core';
import { mdiChartLine } from '@mdi/js';
import { api } from '@/api';
import { DataSetDto } from '@/api/generator';
import { computed, ref } from 'vue';
import { provide } from 'vue';
import TrendChart from '@/components/TrendChart/TrendChart.vue';
import { reloadTrendsKey } from '@/components/TrendChart/types';

const props = defineProps<{
  evaluationResultId?: string;
  disabled?: boolean;
}>();

const [isDialogOpen, toggleDialogOpen] = useToggle(false);

async function getDataset() {
  if (props.evaluationResultId) {
    const { result } = await api.dataSetApi.getDataPointsWithRange({
      dataPointRequestWithRangeDto: {
        evaluationResultId: props.evaluationResultId,
        numberOfPrecedingPoints: 20,
        numberOfSubsequentPoints: 10,
      },
    });

    return result?.dataSets[0];
  } else {
    return undefined;
  }
}

provide(reloadTrendsKey, async () => (dataset.value = await getDataset()));

const isTrendAvailabel = computed(() => props.evaluationResultId !== undefined);

const dataset = ref<DataSetDto>();

async function toggle() {
  if (isTrendAvailabel.value) {
    dataset.value = await getDataset();
    toggleDialogOpen(true);
  }
}
</script>

<template>
  <Tooltip>
    <template #tooltip>{{ $t('Trends') }}</template>
    <template #target="{ props: tooltipProps }">
      <Button
        v-bind="tooltipProps"
        variant="text"
        :icon="mdiChartLine"
        :disabled="!isTrendAvailabel || disabled"
        @click.stop="toggle"
      />
    </template>
  </Tooltip>

  <Dialog v-model="isDialogOpen" size="large">
    <template #text>
      <TrendChart
        v-if="dataset"
        :dataset="dataset"
        showInJobContext
        :markedEvaluationResultIds="evaluationResultId ? [evaluationResultId] : undefined"
      />
      <p v-else>{{ $t('NoData') }}</p>
    </template>
  </Dialog>
</template>
